import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, AdminGroupMember, GroupUserCandidate } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime, getTimestampValue } from '../utils/dateUtils';

type GroupEditorSort = 'newest_signup' | 'last_active' | 'total_meals' | 'active_days' | 'email';

const sortOptions: Array<{ value: GroupEditorSort; label: string }> = [
  { value: 'newest_signup', label: 'Newest signup' },
  { value: 'last_active', label: 'Recently active' },
  { value: 'total_meals', label: 'Most meals' },
  { value: 'active_days', label: 'Most active days' },
  { value: 'email', label: 'Email' },
];

const hasCompleteTargets = (user: GroupUserCandidate): boolean => {
  return user.target_carbs != null && user.target_protein != null && user.target_fat != null;
};

const getLastActiveTimestamp = (user: Pick<GroupUserCandidate, 'last_login' | 'last_meal_logged'>): number => {
  return Math.max(getTimestampValue(user.last_login), getTimestampValue(user.last_meal_logged));
};

const sortUsers = (users: GroupUserCandidate[], sortBy: GroupEditorSort): GroupUserCandidate[] => {
  const nextUsers = [...users];

  nextUsers.sort((left, right) => {
    if (sortBy === 'email') {
      return left.email.localeCompare(right.email);
    }

    if (sortBy === 'newest_signup') {
      return getTimestampValue(right.created_at) - getTimestampValue(left.created_at);
    }

    if (sortBy === 'last_active') {
      return getLastActiveTimestamp(right) - getLastActiveTimestamp(left);
    }

    if (sortBy === 'total_meals') {
      return right.total_meals - left.total_meals;
    }

    return right.active_days - left.active_days;
  });

  return nextUsers;
};

const mergeUsers = (candidateUsers: GroupUserCandidate[], selectedUsers: AdminGroupMember[]): GroupUserCandidate[] => {
  const mergedUsers = new Map<number, GroupUserCandidate>();

  candidateUsers.forEach((user) => {
    mergedUsers.set(user.id, user);
  });

  selectedUsers.forEach((user) => {
    mergedUsers.set(user.id, user);
  });

  return Array.from(mergedUsers.values());
};

const GroupEditor: React.FC = () => {
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();
  const { logout, user: currentUser } = useAuth();

  const parsedGroupId = groupId ? parseInt(groupId, 10) : null;
  const isEditing = parsedGroupId != null;

  const [users, setUsers] = useState<GroupUserCandidate[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [name, setName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<GroupEditorSort>('last_active');
  const [draggedUserId, setDraggedUserId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (isEditing && (parsedGroupId == null || Number.isNaN(parsedGroupId))) {
        setError('Invalid group id.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const usersResponse = await adminAPI.getGroupUsers();
        if (!usersResponse.success) {
          setError(usersResponse.error || 'Failed to load users');
          setIsLoading(false);
          return;
        }

        if (parsedGroupId == null) {
          setUsers(usersResponse.users);
          setSelectedUserIds([]);
          setName('');
          setIsLoading(false);
          return;
        }

        const groupResponse = await adminAPI.getGroup(parsedGroupId);
        if (!groupResponse.success) {
          setError(groupResponse.error || 'Failed to load group');
          setIsLoading(false);
          return;
        }

        setUsers(mergeUsers(usersResponse.users, groupResponse.group.members));
        setSelectedUserIds(groupResponse.group.memberUserIds);
        setName(groupResponse.group.name);
      } catch (err: any) {
        if (err.response?.status === 403) {
          setError('Only super admins can manage groups.');
        } else if (err.response?.status === 404) {
          setError('Group not found.');
        } else {
          setError(isEditing ? 'Failed to load group editor' : 'Failed to load users');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void loadData();
  }, [isEditing, parsedGroupId]);

  const selectedUserIdSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  const filteredSortedUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const visibleUsers = normalizedSearch
      ? users.filter((user) => user.email.toLowerCase().includes(normalizedSearch))
      : users;
    return sortUsers(visibleUsers, sortBy);
  }, [searchTerm, sortBy, users]);

  const availableUsers = useMemo(
    () => filteredSortedUsers.filter((user) => !selectedUserIdSet.has(user.id)),
    [filteredSortedUsers, selectedUserIdSet]
  );

  const selectedUsers = useMemo(
    () => filteredSortedUsers.filter((user) => selectedUserIdSet.has(user.id)),
    [filteredSortedUsers, selectedUserIdSet]
  );

  const hiddenSelectedCount = selectedUserIds.length - selectedUsers.length;

  const addUser = (userId: number) => {
    setSelectedUserIds((currentUserIds) => (
      currentUserIds.includes(userId) ? currentUserIds : [...currentUserIds, userId]
    ));
  };

  const removeUser = (userId: number) => {
    setSelectedUserIds((currentUserIds) => currentUserIds.filter((id) => id !== userId));
  };

  const handleDropIntoGroup = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedValue = event.dataTransfer.getData('text/plain');
    const droppedUserId = droppedValue ? parseInt(droppedValue, 10) : draggedUserId;

    if (droppedUserId && !Number.isNaN(droppedUserId)) {
      addUser(droppedUserId);
    }

    setDraggedUserId(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');

    try {
      const payload = {
        name: name.trim(),
        memberUserIds: selectedUserIds,
      };

      const response = parsedGroupId == null
        ? await adminAPI.createGroup(payload)
        : await adminAPI.updateGroup(parsedGroupId, payload);

      if (!response.success || !response.group) {
        setError(response.error || 'Failed to save group');
        return;
      }

      navigate(`/groups/${response.group.id}`);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Only super admins can manage groups.');
      } else {
        setError('Failed to save group');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(isEditing && parsedGroupId != null ? `/groups/${parsedGroupId}` : '/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              &larr; Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{isEditing ? 'Edit Group' : 'Create Group'}</h1>
              <p className="text-sm text-gray-500">
                Search by email, sort by signup or activity, then add users with one click or drag them into the group.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">{currentUser?.email}</span>
            <button
              onClick={logout}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Example: Spring 2026 coaching cohort"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-3">
              {isEditing && parsedGroupId != null && (
                <button
                  onClick={() => navigate(`/groups/${parsedGroupId}`)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={isSaving || !name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {isSaving ? 'Saving...' : isEditing ? 'Save Group' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Available Users</h2>
                  <p className="text-sm text-gray-500">
                    {availableUsers.length} visible after filters
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search by email..."
                    className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value as GroupEditorSort)}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {availableUsers.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">
                No users match the current search and filter state.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Add
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Signed Up
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Active
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Overall Activity
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {availableUsers.map((user) => (
                      <tr
                        key={user.id}
                        draggable
                        onDoubleClick={() => addUser(user.id)}
                        onDragStart={(event) => {
                          setDraggedUserId(user.id);
                          event.dataTransfer.setData('text/plain', String(user.id));
                        }}
                        onDragEnd={() => setDraggedUserId(null)}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            onClick={() => addUser(user.id)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                          >
                            Add
                          </button>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">{user.email}</div>
                            {Boolean(user.is_trainer) && (
                              <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">
                                Trainer
                              </span>
                            )}
                            {!hasCompleteTargets(user) && (
                              <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                                No targets
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">User ID {user.id}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDateTime(user.created_at)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDateTime((getLastActiveTimestamp(user) > 0 ? (getTimestampValue(user.last_login) >= getTimestampValue(user.last_meal_logged) ? user.last_login : user.last_meal_logged) : null))}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          <div>{user.total_meals} meals</div>
                          <div className="text-xs text-gray-500">{user.active_days} active days</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div
            className="bg-white rounded-lg shadow border-2 border-dashed border-gray-200"
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDropIntoGroup}
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Selected Members</h2>
              <p className="text-sm text-gray-500">
                {selectedUserIds.length} selected. Drop users anywhere in this panel to add them quickly.
              </p>
            </div>

            {selectedUserIds.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">
                No users selected yet.
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {selectedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    onDoubleClick={() => removeUser(user.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.email}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Last active {formatDateTime((getLastActiveTimestamp(user) > 0 ? (getTimestampValue(user.last_login) >= getTimestampValue(user.last_meal_logged) ? user.last_login : user.last_meal_logged) : null))}
                        </div>
                        <div className="text-xs text-gray-500">
                          {user.total_meals} meals across {user.active_days} active days
                        </div>
                      </div>
                      <button
                        onClick={() => removeUser(user.id)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}

                {hiddenSelectedCount > 0 && (
                  <div className="text-xs text-gray-500 px-1">
                    {hiddenSelectedCount} selected member{hiddenSelectedCount === 1 ? '' : 's'} hidden by the current search.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default GroupEditor;
